/**
 * Artifact swap tracker — diagnoses leaks specific to PoiArtifact GLB
 * swap/standby/load cycles (Black Stone ↔ Maqam Ibrahim ↔ Golden Spout
 * within a single SparkRenderer-shell session).
 *
 * Activation: `?mem-debug=1` URL flag (same as memoryTracker). Without it,
 * all functions are no-op and there's zero overhead.
 *
 * What it tracks:
 *
 * 1. **Per-phase heap snapshots** (`logArtifactPhase`) — captures
 *    `usedJSHeapMb` + Three.js `renderer.info` + scene graph counters at
 *    every swap stage (start → preload-done → unload-done → attach-done).
 *    Reveals WHICH stage of the swap is allocating / failing to release.
 *
 * 2. **GC-confirmed leak detection** via `FinalizationRegistry` — every
 *    Texture / Material / BufferGeometry that we expect to be GC'd after
 *    dispose is registered. After 5 seconds we count how many were ACTUALLY
 *    finalized. If `disposed > finalized` → those instances are still alive
 *    in heap → leak. Logs the exact counts so you can see the leaking type.
 *
 * 3. **Object inventory snapshot** (`dumpArtifactInventory`) — exposed on
 *    `window.__dumpArtifact()` for manual triggering from DevTools console.
 *    Walks the scene graph, counts unique materials/textures/geometries,
 *    cross-references with renderer.info to find drift.
 *
 * Why a separate module from `memoryTracker.ts`:
 * - memoryTracker is general-purpose (counters, overlay, periodic snapshots).
 * - This module is FORENSIC — designed to find a specific leak by comparing
 *   creation-vs-finalization counts across swap cycles.
 *
 * Usage in `createGlbScene.ts` (only when `poiArtifactOverlay === true`):
 *   - On unloadContent BEGIN: `trackForFinalization(textures, materials, geometries)`
 *   - At each phase: `logArtifactPhase('swap.preload-start', { … })`
 *   - On window.__dumpArtifact: walk current scene graph
 */

import * as THREE from 'three';
import { logEvent } from './memoryTracker';

const isBrowser = typeof window !== 'undefined';
const enabled = isBrowser && new URLSearchParams(window.location.search).get('mem-debug') === '1';

// ───────────────────────────────────────────────────────────────────────
// FinalizationRegistry — confirms whether GC actually collected disposed objects
// ───────────────────────────────────────────────────────────────────────

interface FinalizationCounter {
  type: 'Texture' | 'Material' | 'BufferGeometry' | 'Mesh';
  /** Sequential disposal id. Increments per registered instance. */
  disposalId: number;
  /** sceneId of the artifact being unloaded — to attribute leaks. */
  sceneId: string;
  /** ms-timestamp at registration. */
  registeredAt: number;
}

/**
 * Per-type counters. `disposed` increments at registration; `finalized`
 * increments when GC actually collects the registered object.
 *
 * After a swap, look at `disposed - finalized` per type. If positive after
 * 5+ seconds, those instances leaked.
 */
interface TypeStats {
  disposed: number;
  finalized: number;
  /** Sample of disposalIds that haven't been finalized yet (for diagnosis). */
  pendingIds: Set<number>;
}

const stats: Record<FinalizationCounter['type'], TypeStats> = {
  Texture: { disposed: 0, finalized: 0, pendingIds: new Set() },
  Material: { disposed: 0, finalized: 0, pendingIds: new Set() },
  BufferGeometry: { disposed: 0, finalized: 0, pendingIds: new Set() },
  Mesh: { disposed: 0, finalized: 0, pendingIds: new Set() },
};

let nextDisposalId = 1;

const finalizationRegistry =
  enabled && typeof FinalizationRegistry !== 'undefined'
    ? new FinalizationRegistry<FinalizationCounter>((counter) => {
        const s = stats[counter.type];
        s.finalized++;
        s.pendingIds.delete(counter.disposalId);
      })
    : null;

/**
 * Register an object for finalization tracking. Call this BEFORE disposing
 * the object. After GC eventually collects it, the registry callback fires
 * and increments `finalized` count.
 *
 * Returns the assigned disposalId for cross-referencing in logs.
 */
export const trackForFinalization = (
  obj: object,
  type: FinalizationCounter['type'],
  sceneId: string,
): number => {
  if (!enabled || !finalizationRegistry) return -1;
  const disposalId = nextDisposalId++;
  const counter: FinalizationCounter = {
    type,
    disposalId,
    sceneId,
    registeredAt: performance.now(),
  };
  stats[type].disposed++;
  stats[type].pendingIds.add(disposalId);
  finalizationRegistry.register(obj, counter);
  return disposalId;
};

/** Dump current finalization stats — call before/after swap to see deltas. */
export const dumpFinalizationStats = (label: string): void => {
  if (!enabled) return;
  logEvent(`artifact: finalization stats — ${label}`, {
    Texture: { ...stats.Texture, pendingCount: stats.Texture.pendingIds.size },
    Material: { ...stats.Material, pendingCount: stats.Material.pendingIds.size },
    BufferGeometry: { ...stats.BufferGeometry, pendingCount: stats.BufferGeometry.pendingIds.size },
    Mesh: { ...stats.Mesh, pendingCount: stats.Mesh.pendingIds.size },
  });
};

/**
 * Schedule a delayed finalization check (default +5s). At that point GC
 * usually has had at least one minor cycle. Compares disposed vs finalized
 * counts and logs the delta — a positive delta means leaks.
 */
export const scheduleFinalizationCheck = (
  swapLabel: string,
  delayMs = 5000,
): void => {
  if (!enabled) return;
  setTimeout(() => {
    const summary = (Object.entries(stats) as Array<[FinalizationCounter['type'], TypeStats]>).map(
      ([type, s]) => ({
        type,
        disposed: s.disposed,
        finalized: s.finalized,
        leaked: s.disposed - s.finalized,
        pendingCount: s.pendingIds.size,
      }),
    );
    logEvent(`artifact: finalization check (+${delayMs}ms) — ${swapLabel}`, {
      summary,
      hasLeak: summary.some((row) => row.leaked > 0),
    });
  }, delayMs);
};

// ───────────────────────────────────────────────────────────────────────
// Phase logging — heap + renderer.info + scene graph at each swap stage
// ───────────────────────────────────────────────────────────────────────

interface ArtifactPhaseSnapshot {
  /** Three.js renderer with `info` field. */
  renderer?: THREE.WebGLRenderer | null;
  /** Scene root for graph traversal. */
  scene?: THREE.Scene | null;
  /** Currently active artifact sceneId (or 'standby' / 'unknown'). */
  sceneId?: string;
  /** Optional extra context (e.g. modelRoots count, mixers count). */
  extra?: Record<string, unknown>;
}

/**
 * Log a snapshot at a named swap phase. Captures:
 *   - usedJSHeapMb (Chrome/Android only, null on Safari iOS)
 *   - renderer.info.memory.{textures,geometries}
 *   - renderer.info.programs.length
 *   - scene.children count
 *   - traversed unique Material/Texture/Geometry counts
 *
 * Use to identify WHICH phase of the swap is allocating / not releasing.
 *
 * Example trace for one swap cycle:
 *   artifact: phase swap.start         { tex:42 geo:18 mat:12 children:8 ... }
 *   artifact: phase swap.preload-done  { tex:42 geo:18 mat:12 children:8 ... }  ← same
 *   artifact: phase swap.unload-done   { tex:18 geo:8  mat:5  children:3 ... }  ← old released
 *   artifact: phase swap.attach-done   { tex:42 geo:18 mat:12 children:8 ... }  ← new allocated
 *
 * If unload-done shows tex/geo/mat NOT decreasing → leak in unload path.
 * If attach-done shows MORE than start → growing per cycle = leak.
 */
export const logArtifactPhase = (
  phase: string,
  snap: ArtifactPhaseSnapshot,
): void => {
  if (!enabled) return;

  let rendererTextures: number | null = null;
  let rendererGeometries: number | null = null;
  let rendererPrograms: number | null = null;
  if (snap.renderer?.info) {
    rendererTextures = snap.renderer.info.memory?.textures ?? null;
    rendererGeometries = snap.renderer.info.memory?.geometries ?? null;
    rendererPrograms = snap.renderer.info.programs?.length ?? null;
  }

  let sceneChildren: number | null = null;
  let sceneTotalObjects = 0;
  let sceneMeshes = 0;
  let uniqueMaterials = 0;
  let uniqueGeometries = 0;
  let uniqueTextures = 0;
  if (snap.scene) {
    sceneChildren = snap.scene.children.length;
    const seenMat = new Set<unknown>();
    const seenGeo = new Set<unknown>();
    const seenTex = new Set<unknown>();
    snap.scene.traverse((obj) => {
      sceneTotalObjects++;
      if ((obj as THREE.Mesh).isMesh) {
        sceneMeshes++;
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry && !seenGeo.has(mesh.geometry)) {
          seenGeo.add(mesh.geometry);
          uniqueGeometries++;
        }
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const m of mats) {
          if (!m || seenMat.has(m)) continue;
          seenMat.add(m);
          uniqueMaterials++;
          // Walk all enumerable fields for textures (same pattern as
          // disposeMaterialTextures — catches every PBR map).
          for (const key in m) {
            const v = (m as unknown as Record<string, unknown>)[key];
            if (v && (v as { isTexture?: boolean }).isTexture && !seenTex.has(v)) {
              seenTex.add(v);
              uniqueTextures++;
            }
          }
        }
      }
    });
  }

  logEvent(`artifact: phase ${phase}`, {
    sceneId: snap.sceneId ?? 'unknown',
    rendererTextures,
    rendererGeometries,
    rendererPrograms,
    sceneChildren,
    sceneTotalObjects,
    sceneMeshes,
    uniqueMaterials,
    uniqueGeometries,
    uniqueTextures,
    ...(snap.extra ?? {}),
  });
};

// ───────────────────────────────────────────────────────────────────────
// Manual inventory dump — exposed on window for DevTools triggering
// ───────────────────────────────────────────────────────────────────────

/**
 * Install a `window.__dumpArtifact()` console helper. Returns current
 * artifact-related state when called manually. Useful for snapshotting
 * BEFORE/AFTER user manually performs an action (open, close, swap).
 *
 * Call from createGlbScene.ts when poiArtifactOverlay is true:
 *   installDumpHook(() => ({
 *     sceneId: currentConfig.id,
 *     renderer,
 *     scene,
 *     modelRoots,
 *     mixers,
 *     collisionSystem,
 *   }));
 */
export const installDumpHook = (
  getState: () => {
    sceneId?: string;
    renderer?: THREE.WebGLRenderer | null;
    scene?: THREE.Scene | null;
    extra?: Record<string, unknown>;
  },
): (() => void) | undefined => {
  if (!enabled) return undefined;
  const win = window as unknown as Record<string, unknown>;
  const dump = () => {
    const state = getState();
    logArtifactPhase('manual dump', {
      sceneId: state.sceneId,
      renderer: state.renderer ?? null,
      scene: state.scene ?? null,
      extra: state.extra,
    });
    dumpFinalizationStats('manual dump');
  };
  win.__dumpArtifact = dump;
  // Return uninstall fn (call on dispose). Idempotent.
  return () => {
    if (win.__dumpArtifact === dump) {
      delete win.__dumpArtifact;
    }
  };
};

// ───────────────────────────────────────────────────────────────────────
// Convenience: track all textures/materials/geometries reachable from a
// scene root. Use this BEFORE disposing them to register everything.
// ───────────────────────────────────────────────────────────────────────

/**
 * Walk the given roots, find every unique Texture/Material/BufferGeometry,
 * and register each for finalization tracking.
 *
 * Returns counts for logging.
 *
 * IMPORTANT: this MUST be called BEFORE you dispose the objects. Once
 * disposed, traversal still finds them but they may be partially destroyed.
 * The registry merely watches for GC of the JS object itself, which can
 * happen any time after all strong refs to it are dropped.
 */
export const trackTreeForFinalization = (
  roots: Iterable<THREE.Object3D>,
  sceneId: string,
): { textures: number; materials: number; geometries: number; meshes: number } => {
  if (!enabled) {
    return { textures: 0, materials: 0, geometries: 0, meshes: 0 };
  }
  const seenMat = new Set<unknown>();
  const seenGeo = new Set<unknown>();
  const seenTex = new Set<unknown>();
  let textures = 0;
  let materials = 0;
  let geometries = 0;
  let meshes = 0;
  for (const root of roots) {
    root.traverse((obj) => {
      if (!(obj as THREE.Mesh).isMesh) return;
      const mesh = obj as THREE.Mesh;
      meshes++;
      trackForFinalization(mesh, 'Mesh', sceneId);
      if (mesh.geometry && !seenGeo.has(mesh.geometry)) {
        seenGeo.add(mesh.geometry);
        geometries++;
        trackForFinalization(mesh.geometry, 'BufferGeometry', sceneId);
      }
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) {
        if (!m || seenMat.has(m)) continue;
        seenMat.add(m);
        materials++;
        trackForFinalization(m, 'Material', sceneId);
        for (const key in m) {
          const v = (m as unknown as Record<string, unknown>)[key];
          if (v && (v as { isTexture?: boolean }).isTexture && !seenTex.has(v)) {
            seenTex.add(v);
            textures++;
            trackForFinalization(v as THREE.Texture, 'Texture', sceneId);
          }
        }
      }
    });
  }
  return { textures, materials, geometries, meshes };
};
